import { initializeApp } from "firebase/app";
import {
  browserLocalPersistence,
  browserPopupRedirectResolver,
  browserSessionPersistence,
  indexedDBLocalPersistence,
  initializeAuth,
  type Auth,
} from "firebase/auth";
// Internal, for `warmUpPopupResolver` alone. `firebase` pins `@firebase/auth` exactly, and this entry
// shares its module with the one `firebase/auth` re-exports, so it is the same instance cache.
import { _getInstance } from "@firebase/auth/internal";

const config = {
  apiKey: "AIzaSyBEdUrtmJ6N157dGXRXU1gjT4jJieG_c6E",
  authDomain: "account.starthaven.com",
  projectId: "mirrortab-trusty-desktop-auth",
  appId: "1:248047969125:web:078ea63bfd36629cec778c",
  messagingSenderId: "248047969125",
};

/**
 * Where the signed-in user is kept: exactly the list and order `getAuth()` uses (@firebase/auth
 * 1.10.8, `getAuth` in dist/esm2017/index-*.js). The SDK reads the user from the first of these that
 * holds one, so a different list or order would sign people out or lose a session. Where none is
 * available (site data blocked, ENG-3411) it falls back to memory, as before.
 */
export const AUTH_PERSISTENCE = [indexedDBLocalPersistence, browserLocalPersistence, browserSessionPersistence];

export const firebaseApp = initializeApp(config);

/**
 * ENG-3625: `getAuth()` minus its `popupRedirectResolver`. With the resolver, on mobile browsers and
 * Safari (`BrowserPopupRedirectResolver._shouldInitProactively`) the SDK loads its sign-in iframe
 * (Google's gapi loader, then `/__/auth/iframe` and its script) BEFORE the first auth state, and the
 * page shows a spinner until then. The resolver is instead passed at each popup call
 * (`browserPopupRedirectResolver`), and `warmUpPopupResolver` loads the iframe while a button that opens
 * a popup is on screen (hooks/usePopupSignInReady.ts).
 *
 * Nothing here signs in by redirect, and nothing ever has, so no saved user carries the redirect
 * marker that would make the SDK need a resolver while it starts up (`initializeCurrentUser`).
 * Add a redirect flow and it will: the resolver goes back here.
 *
 * `getAuth()` also read `__FIREBASE_DEFAULTS__` (an emulator host, `authTokenSyncURL`); `initializeAuth`
 * does not, and this app never set either.
 */
export const auth = initializeAuth(firebaseApp, { persistence: AUTH_PERSISTENCE });

type PopupResolverInternal = { _initialize(auth: Auth): Promise<unknown> };

let warming: Promise<unknown> | null = null;
let ready = false;
const settledListeners = new Set<(ready: boolean) => void>();

function settle(outcome: boolean): void {
  settledListeners.forEach((listener) => {
    try {
      listener(outcome);
    } catch {
      // One listener failing must not keep the others from hearing.
    }
  });
}

/**
 * Loads the popup sign-in iframe now, so it is ready before anyone presses a sign-in button.
 *
 * Every popup call (`signInWithPopup`, `reauthenticateWithPopup`) awaits
 * `resolver._initialize(auth)` before it calls `window.open` (`AbstractPopupRedirectOperation.execute`,
 * then `PopupOperation.onExecution`). While the iframe is still loading, that wait is network time, and
 * Safari and iOS block a window not opened in the press itself: what `getAuth()` loads it early to
 * avoid. Once it has loaded, the wait is a resolved promise, as it is today.
 *
 * There is no public API for this. `_getInstance` returns the one resolver instance the popup calls
 * use (it caches one per class, core/util/instantiator.ts), and `_initialize` is the call the SDK makes
 * itself at startup (`AuthImpl._initializeWithPersistence`). It is idempotent there too: a load in
 * flight is shared, and a failed one is forgotten so the next call retries. After a popup call has
 * loaded the iframe itself, a call here resolves at once and only brings `popupResolverReady` up to date.
 *
 * Best effort: it never throws and never rejects. If it fails, a popup call loads the iframe itself.
 */
export function warmUpPopupResolver(): void {
  if (warming) return;
  try {
    const resolver = _getInstance<PopupResolverInternal>(browserPopupRedirectResolver);
    warming = resolver._initialize(auth).then(
      () => { ready = true; settle(true); },
      () => { warming = null; settle(false); },
    );
  } catch {
    warming = null;
    settle(false);
  }
}

/** Whether the popup sign-in iframe has loaded, so a press opens the window at once. */
export const popupResolverReady = (): boolean => ready;

/**
 * Calls `listener` each time a warm-up finishes: `true` once the iframe has loaded, `false` when a
 * load fails (a later warm-up may still succeed). Returns the unsubscribe.
 */
export function onPopupResolverSettled(listener: (ready: boolean) => void): () => void {
  settledListeners.add(listener);
  return () => { settledListeners.delete(listener); };
}
